import type { CohesiveAPIClient } from "@cohesive/api-client";
import React, { useMemo } from "react";
import { ElementsContext, type ElementsContextValue } from "./ElementsContext";

export interface ElementsProviderProps {
  /** The base API client instance */
  apiClient: CohesiveAPIClient;
  /** Optional organization ID for impersonation (admin dashboard use case) */
  organizationId?: string;
  /** Optional user ID for impersonation (admin dashboard use case) */
  userId?: string;
  children: React.ReactNode;
}

/**
 * ElementsProvider wraps children with a configured API client context.
 *
 * For user-dashboard: No impersonation needed, just pass the apiClient
 * For admin-dashboard: Pass organizationId and userId to impersonate
 *
 * @example
 * // User Dashboard - no impersonation
 * <ElementsProvider apiClient={apiClient}>
 *   <SomeComponent ... />
 * </ElementsProvider>
 *
 * @example
 * // Admin Dashboard - with impersonation
 * <ElementsProvider apiClient={apiClient} organizationId={orgId} userId={userId}>
 *   <SomeComponent ... />
 * </ElementsProvider>
 */
export function ElementsProvider({
  apiClient,
  organizationId,
  userId,
  children,
}: ElementsProviderProps) {
  // Create configured client with impersonation if needed
  const configuredClient = useMemo(() => {
    let client = apiClient;
    if (organizationId) {
      client = client.org(organizationId);
    }
    if (userId) {
      client = client.uid(userId);
    }
    return client;
  }, [apiClient, organizationId, userId]);

  const contextValue: ElementsContextValue = useMemo(
    () => ({
      client: configuredClient,
      organizationId,
      userId,
    }),
    [configuredClient, organizationId, userId],
  );

  return (
    <ElementsContext.Provider value={contextValue}>
      {children}
    </ElementsContext.Provider>
  );
}
